/**
 * notificationStore — framework-agnostic singleton for the in-app
 * notification center (the header bell).
 *
 * Mirrors the `tvSocket` singleton pattern so any module (React component,
 * API layer, socket handler) can `push()` a notification without prop-drilling
 * or a Context. The header's <NotificationBell /> subscribes via the
 * `useNotifications` hook (useSyncExternalStore).
 *
 * Notifications are persisted to localStorage so they survive a refresh, are
 * capped at MAX_ITEMS, and dropped after RETENTION_MS. Rapid duplicates of the
 * same (type, deviceId) inside DEDUPE_MS are collapsed so a screen that flaps
 * or a bulk schedule-save loop doesn't spam the feed.
 *
 * Notification shape:
 *   {
 *     id: string,
 *     type: string,            // e.g. 'screen-offline', 'schedule-updated'
 *     severity: 'info' | 'success' | 'warning' | 'critical',
 *     title: string,
 *     message: string,
 *     deviceId?: string,       // optional — links to a screen
 *     projectId?: string,      // optional — the city this belongs to; null = all cities
 *     timestamp: number,       // ms epoch
 *     read: boolean,
 *   }
 *
 * The feed is city-scoped: each notification is tagged with the projectId
 * (city) it originated in, and the bell filters to the active city (a null
 * projectId is treated as "all cities"). See `useNotifications(cityFilter)`.
 */

const STORAGE_KEY = 'cms_notifications';
const MAX_ITEMS = 100;
const RETENTION_MS = 7 * 24 * 60 * 60 * 1000; // 7 days
const DEDUPE_MS = 8000; // collapse repeats of the same type+device within 8s

const VALID_SEVERITY = new Set(['info', 'success', 'warning', 'critical']);

function makeId() {
  try {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  } catch {
    /* ignore */
  }
  return `n_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
}

class NotificationStore {
  constructor() {
    this.items = this._load();
    this.listeners = new Set();
  }

  _load() {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      if (!Array.isArray(parsed)) return [];
      const cutoff = Date.now() - RETENTION_MS;
      return parsed
        .filter((n) => n && typeof n === 'object' && n.timestamp > cutoff)
        .slice(0, MAX_ITEMS);
    } catch {
      return [];
    }
  }

  _persist() {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(this.items));
    } catch {
      /* localStorage full / unavailable — non-fatal, in-memory still works */
    }
  }

  _emit() {
    for (const fn of this.listeners) {
      try {
        fn();
      } catch {
        /* a bad subscriber shouldn't break the others */
      }
    }
  }

  /**
   * Add a notification. Returns the created (or deduped) item.
   * @param {{type?:string, severity?:string, title:string, message?:string, deviceId?:string}} input
   */
  push(input = {}) {
    const now = Date.now();
    const severity = VALID_SEVERITY.has(input.severity) ? input.severity : 'info';
    const type = input.type || 'general';
    const deviceId = input.deviceId || null;
    const projectId = input.projectId || null;

    // Dedupe: if the most recent item is the same type+device+city within the
    // window, just refresh its timestamp + mark it unread again instead of
    // stacking a near-identical entry.
    const recent = this.items[0];
    if (
      recent &&
      recent.type === type &&
      (recent.deviceId || null) === deviceId &&
      (recent.projectId || null) === projectId &&
      now - recent.timestamp < DEDUPE_MS
    ) {
      recent.timestamp = now;
      recent.read = false;
      if (input.message) recent.message = input.message;
      if (input.title) recent.title = input.title;
      this._persist();
      this._emit();
      return recent;
    }

    const item = {
      id: makeId(),
      type,
      severity,
      title: input.title || 'Notification',
      message: input.message || '',
      deviceId,
      projectId,
      timestamp: now,
      read: false,
    };

    this.items = [item, ...this.items].slice(0, MAX_ITEMS);
    this._persist();
    this._emit();
    return item;
  }

  /** Returns the current notifications array (stable reference until mutated). */
  getAll() {
    return this.items;
  }

  getUnreadCount() {
    let n = 0;
    for (const it of this.items) if (!it.read) n += 1;
    return n;
  }

  markRead(id) {
    let changed = false;
    this.items = this.items.map((it) => {
      if (it.id === id && !it.read) {
        changed = true;
        return { ...it, read: true };
      }
      return it;
    });
    if (changed) {
      this._persist();
      this._emit();
    }
  }

  /**
   * Mark notifications read. With no argument, marks all. With an array of
   * ids, marks only those (used to scope "mark read" to the visible city).
   * @param {string[]|null} [ids]
   */
  markAllRead(ids = null) {
    const idSet = Array.isArray(ids) ? new Set(ids) : null;
    let changed = false;
    this.items = this.items.map((it) => {
      if (it.read) return it;
      if (idSet && !idSet.has(it.id)) return it;
      changed = true;
      return { ...it, read: true };
    });
    if (changed) {
      this._persist();
      this._emit();
    }
  }

  remove(id) {
    const next = this.items.filter((it) => it.id !== id);
    if (next.length !== this.items.length) {
      this.items = next;
      this._persist();
      this._emit();
    }
  }

  /**
   * Remove notifications. With no argument, clears all. With an array of ids,
   * removes only those (used to scope "clear all" to the visible city).
   * @param {string[]|null} [ids]
   */
  clearAll(ids = null) {
    if (this.items.length === 0) return;
    if (Array.isArray(ids)) {
      const idSet = new Set(ids);
      const next = this.items.filter((it) => !idSet.has(it.id));
      if (next.length === this.items.length) return;
      this.items = next;
    } else {
      this.items = [];
    }
    this._persist();
    this._emit();
  }

  /**
   * Subscribe to store changes. Returns an unsubscribe function.
   * @param {() => void} listener
   */
  subscribe(listener) {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }
}

const notificationStore = new NotificationStore();
export default notificationStore;
